import { AcaoNotaFiscalEntradaEnum } from '../enums/suprimentos/acao-nota-fiscal-entrada.enum';
import { OrigemNFeEntradaEnum } from '../enums/repo-dfe/origem-nfe-entrada.enum';
import { StatusNFeEntradaEnum } from '../enums/repo-dfe/status-nfe-entrada.enum';
import { AcaoNotaFiscalEntradaStruct } from '../services/structs/repo-dfe/acao-nota-fiscal-entrada.struct';
import { NotaFiscalEntradaListStruct } from '../services/structs/repo-dfe/nota-fiscal-entrada-list.struct';
import { isStatusParcial } from './nota-fiscal-entrada-status.helper';
import { isManifestacaoBloqueada } from './manifestacao-nfe.helper';

/**
 * Monta o menu de contexto da nota conforme o status da nota. A primeira ação é a principal do status,
 * "Ver histórico" vale para todos e "Excluir nota" fica sempre por último. Só aparece desabilitado, com motivo
 * em tooltip, o que depende de condição dentro do status (permissão, flag lançada, processo de compras).
 * Lançar e excluir seguem as flags da nota (isEstoqueLancado, isFinanceiroLancado): com a flag ligada, Excluir;
 * desligada, Lançar. Nota sem item de produto (só de serviços) não tem estoque nem formação de preços:
 * "Lançar estoque" e "Formação de preços" não aparecem.
 * Lançar vale para quem pode inserir a nota; excluir estoque e excluir financeiro têm permissão
 * própria cada um. Excluir não depende do status: a nota cancelada pelo emitente mantém Excluir enquanto a flag
 * estiver ligada. A listagem da empresa principal mostra as notas do grupo: na nota de outra empresa, lançar e
 * excluir ficam desabilitados, porque o orquestrador procura a nota na empresa do token.
 */
export function acoesNotaFiscalEntrada(
  nota: NotaFiscalEntradaListStruct,
  podeLancar: boolean,
  podeExcluirEstoque: boolean,
  podeExcluirFinanceiro: boolean,
  podeManifestar: boolean,
  idEmpresaToken: number
): AcaoNotaFiscalEntradaStruct[] {
  const status = nota.idStatusNFeEntrada;
  const isManual = nota.idOrigemNFeEntrada === OrigemNFeEntradaEnum.LancamentoManual;
  const isSefaz = nota.idOrigemNFeEntrada === OrigemNFeEntradaEnum.Sefaz;
  const temChave = !!nota.chaveAcesso;
  // Trava de excluir e reabrir: estoque ou financeiro lançado.
  const temLancamento = nota.isEstoqueLancado || nota.isFinanceiroLancado;
  const semPermissaoSefaz = 'Você não tem permissão para manifestar ou consultar a NF-e na SEFAZ.';
  const isOutraEmpresa = nota.idEmpresa !== idEmpresaToken;
  const outraEmpresa = 'Lance pela empresa da nota.';

  const visualizar: LinhaAcao = [AcaoNotaFiscalEntradaEnum.Visualizar, 'Visualizar', 'visibility', true, ''];
  const copiarChave: LinhaAcao = [
    AcaoNotaFiscalEntradaEnum.CopiarChaveAcesso,
    'Copiar chave de acesso',
    'content_copy',
    temChave,
    'Nota sem chave de acesso.',
  ];
  const verDanfe: LinhaAcao = [AcaoNotaFiscalEntradaEnum.VerDanfe, 'Ver DANFE', 'picture_as_pdf', true, ''];
  const baixarXml: LinhaAcao = [AcaoNotaFiscalEntradaEnum.BaixarXml, 'Baixar XML', 'file_download', true, ''];
  const manifestar: LinhaAcao = [
    AcaoNotaFiscalEntradaEnum.Manifestar,
    'Manifestar',
    'gavel',
    podeManifestar && !isManifestacaoBloqueada(nota.isCancelada, nota.idTipoEventoManifestacaoAtual),
    podeManifestar ? 'A operação já foi confirmada na SEFAZ.' : semPermissaoSefaz,
  ];
  const editar: LinhaAcao = [AcaoNotaFiscalEntradaEnum.Editar, 'Editar', 'edit', true, ''];
  const alterarTags: LinhaAcao = [AcaoNotaFiscalEntradaEnum.Marcadores, 'Alterar tags', 'label', true, ''];
  const verHistorico: LinhaAcao = [AcaoNotaFiscalEntradaEnum.VerHistorico, 'Ver histórico', 'history', true, ''];
  // Lançada é somente leitura: precisa ser reaberta antes de excluir; com estoque ou financeiro lançado, excluí-los vem antes.
  const excluir: LinhaAcao = [
    AcaoNotaFiscalEntradaEnum.ExcluirNota,
    'Excluir nota',
    'delete',
    status !== StatusNFeEntradaEnum.Lancada && !temLancamento,
    status === StatusNFeEntradaEnum.Lancada
      ? 'Reabra a nota para edição antes de excluir.'
      : 'Exclua o estoque e o financeiro lançados antes de excluir a nota.',
  ];

  const excluirEstoque: LinhaAcao = [
    AcaoNotaFiscalEntradaEnum.ExcluirEstoque,
    'Excluir estoque',
    'inventory_2',
    podeExcluirEstoque && !isOutraEmpresa,
    isOutraEmpresa ? outraEmpresa : 'Você não tem permissão para excluir o estoque desta nota.',
  ];
  // A recusa por conta paga ou liquidada vem do Finanças na hora de excluir.
  const excluirFinanceiro: LinhaAcao = [
    AcaoNotaFiscalEntradaEnum.ExcluirFinanceiro,
    'Excluir financeiro',
    'payments',
    podeExcluirFinanceiro && !isOutraEmpresa,
    isOutraEmpresa ? outraEmpresa : 'Você não tem permissão para excluir o financeiro desta nota.',
  ];

  // Nota cancelada pelo emitente só permite consultar, copiar a chave, excluir o que estiver lançado e ver o histórico.
  if (nota.isCancelada) {
    return montar([
      visualizar,
      copiarChave,
      ...(nota.isEstoqueLancado ? [excluirEstoque] : []),
      ...(nota.isFinanceiroLancado ? [excluirFinanceiro] : []),
      verHistorico,
    ]);
  }

  if (status === StatusNFeEntradaEnum.CapturadaSefaz) {
    if (isStatusParcial(status, nota.idDisponibilidadeDocumentoSefaz)) {
      // Resumo: sem manifestação, registra a ciência; com ela, a SEFAZ libera a consulta do XML completo.
      const temManifestacao = nota.idTipoEventoManifestacaoAtual !== null || nota.isManifestacaoConclusivaNaoIdentificada;
      const principal: LinhaAcao = temManifestacao
        ? [
            AcaoNotaFiscalEntradaEnum.ConsultarXml,
            'Consultar XML',
            'sync',
            podeManifestar && temChave,
            podeManifestar ? 'Nota sem chave de acesso.' : semPermissaoSefaz,
          ]
        : [AcaoNotaFiscalEntradaEnum.RegistrarCiencia, 'Registrar ciência', 'task_alt', podeManifestar, semPermissaoSefaz];
      return montar([principal, visualizar, copiarChave, verHistorico]);
    }

    return montar([
      [AcaoNotaFiscalEntradaEnum.TratarNota, 'Tratar nota', 'fact_check', true, ''],
      verDanfe,
      baixarXml,
      manifestar,
      copiarChave,
      verHistorico,
      excluir,
    ]);
  }

  if (status === StatusNFeEntradaEnum.EmLancamento) {
    if (isManual) return montar([editar, alterarTags, verHistorico, excluir]);

    // Sem item pendente a mesma ação reabre o modal para refazer a associação.
    const continuarAssociacao: LinhaAcao = [
      AcaoNotaFiscalEntradaEnum.ContinuarAssociacaoItens,
      nota.possuiAssociacaoPendente ? 'Continuar associação de itens' : 'Refazer associação de itens',
      'merge',
      true,
      '',
    ];
    return montar([
      ...(nota.possuiAssociacaoPendente ? [continuarAssociacao, editar] : [editar, continuarAssociacao]),
      alterarTags,
      verDanfe,
      baixarXml,
      ...(isSefaz ? [manifestar] : []),
      verHistorico,
      excluir,
    ]);
  }

  const lancarEstoque: LinhaAcao = [
    AcaoNotaFiscalEntradaEnum.LancarEstoque,
    'Lançar estoque',
    'inventory_2',
    nota.restricoesOperacionaisCompra.podeLancarEstoque && podeLancar && !isOutraEmpresa,
    isOutraEmpresa
      ? outraEmpresa
      : !nota.restricoesOperacionaisCompra.podeLancarEstoque
      ? nota.restricoesOperacionaisCompra.motivoBloqueioEstoque || 'O processo de compras ainda não permite lançar estoque.'
      : 'Você não tem permissão para lançar o estoque desta nota.',
  ];
  // Nota só de serviços conclui sem estoque: sem item de produto não há o que lançar.
  const acoesEstoque: LinhaAcao[] = nota.isEstoqueLancado
    ? [excluirEstoque]
    : nota.possuiItemProduto
    ? [lancarEstoque]
    : [];
  const acaoFinanceira: LinhaAcao = nota.isFinanceiroLancado
    ? excluirFinanceiro
    : [
        AcaoNotaFiscalEntradaEnum.LancarFinanceiro,
        'Lançar financeiro',
        'payments',
        nota.restricoesOperacionaisCompra.podeLancarFinanceiro && podeLancar && !isOutraEmpresa,
        isOutraEmpresa
          ? outraEmpresa
          : !nota.restricoesOperacionaisCompra.podeLancarFinanceiro
          ? nota.restricoesOperacionaisCompra.motivoBloqueioFinanceiro || 'O processo de compras ainda não permite lançar o financeiro.'
          : 'Você não tem permissão para lançar o financeiro desta nota.',
      ];

  // Lançada: somente leitura até ser reaberta; com estoque ou financeiro lançado, excluí-los vem antes de reabrir.
  return montar([
    visualizar,
    ...(isManual ? [] : [verDanfe, baixarXml]),
    ...(isSefaz ? [manifestar] : []),
    // O item de serviço fica fora da formação de preços: sem item de produto não há o que formar.
    ...(nota.possuiItemProduto
      ? [[AcaoNotaFiscalEntradaEnum.FormacaoPrecos, 'Formação de preços', 'sell', true, ''] as LinhaAcao]
      : []),
    alterarTags,
    ...acoesEstoque,
    acaoFinanceira,
    [
      AcaoNotaFiscalEntradaEnum.ReabrirParaEdicao,
      'Reabrir para edição',
      'restart_alt',
      !temLancamento,
      'Exclua o estoque e o financeiro antes de reabrir a nota.',
    ],
    verHistorico,
    excluir,
  ]);
}

type LinhaAcao = [AcaoNotaFiscalEntradaEnum, string, string, boolean, string];

function montar(linhas: LinhaAcao[]): AcaoNotaFiscalEntradaStruct[] {
  return linhas.map(([acao, rotulo, icone, isDisponivel, motivoIndisponivel]) => {
    const item = new AcaoNotaFiscalEntradaStruct();
    item.acao = acao;
    item.rotulo = rotulo;
    item.icone = icone;
    item.isDisponivel = isDisponivel;
    item.motivoIndisponivel = isDisponivel ? '' : motivoIndisponivel;
    return item;
  });
}
